/**
 * The study summary on the centre screen (2026-10-05, the user's reference image): headline
 * numbers on the left, the study's sites on a globe in the middle with the study's design
 * under it, enrolment progress and milestones on the right, subject status along the foot,
 * and the room (its code, who is in it, that the meeting is written down) at the bottom.
 * Every number opens the dashboard that explains it on the same screen. Drawn at the
 * dashboards' logical size and scaled up with the screen, redrawn only when the data, the
 * room filter or the people change.
 *
 * 2026-10-07: the entrance briefing board was retired (the user's request); what it showed
 * beyond this summary (the design, four more numbers, the room code and the people) is here.
 * The same day the screen widened to 9 m: the canvas is 1.5 panels wide and the numbers stand
 * in three columns, with a wider enrolment curve.
 */
import type * as THREE from 'three';
import type { DashboardFilters, DashboardId, Role, SubjectStatus } from '@clinspace/shared';
import { DASHBOARD_TITLES, ROLE_STYLES, SUBJECT_STATUSES } from '@clinspace/shared';
import { CanvasPanel, PANEL_PX_H, PANEL_PX_W } from './CanvasPanel.ts';
import {
  PALETTE,
  fillRoundRect,
  fitSize,
  font,
  glassPanel,
  panelBackground,
  text,
  uiMode,
  type Rect,
} from './charts.ts';
import { PANEL_H, PANEL_W } from './Dashboard.ts';
import { CENTRE } from '../world/plan.ts';
import type { StudyView } from './StudyView.ts';
import { summaryStats, type StudySummaryStats } from './summary.ts';
import { drawCumulative, drawGlobe } from '../world/horizon.ts';

interface SummaryRegion {
  dashboard: DashboardId;
}

/** The room the summary stands in: its code, the people in it, whether voice is transcribed. */
export interface SummaryRoom {
  code: string;
  people: { name: string; role: Role }[];
  voiceTranscribed: boolean;
}

/** Subject status colours along the foot, in the schema's order. */
const STATUS_COLOURS: Record<SubjectStatus, string> = {
  Screening: PALETTE.grey,
  'Screen Failed': PALETTE.crimson,
  Enrolled: PALETTE.navy,
  'On Treatment': PALETTE.teal,
  Completed: PALETTE.forest,
  Discontinued: PALETTE.amber,
  'Lost to Follow-up': PALETTE.violet,
};

export class StudySummary {
  readonly panel: CanvasPanel;
  private filters: DashboardFilters = {};
  private stats: StudySummaryStats;
  private room: SummaryRoom | undefined;
  private roomKey = '';
  /** Bumped whenever the numbers change (the console refreshes its thumbnail). */
  version = 0;

  constructor(
    private readonly view: StudyView,
    private readonly handlers: {
      project(id: DashboardId): void;
      tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
    },
  ) {
    this.panel = new CanvasPanel({
      width: PANEL_W * CENTRE.wide,
      height: PANEL_H,
      pixelWidth: Math.round(PANEL_PX_W * CENTRE.wide),
      pixelHeight: PANEL_PX_H,
      name: 'study-summary',
      glass: uiMode() === 'chamber',
      resolution: 2,
    });
    this.stats = summaryStats(view, {});
    this.panel.setDraw((ctx, panel) => this.draw(ctx, panel));
    this.panel.onSelect = (region) => {
      const data = region.data as SummaryRegion | undefined;
      if (data) handlers.project(data.dashboard);
    };
    this.panel.onHoverChange = (region, point) =>
      handlers.tooltip(region?.tooltip, region ? point : undefined);
  }

  get summary(): StudySummaryStats {
    return this.stats;
  }

  setFilters(f: DashboardFilters): void {
    this.filters = { ...f };
    this.refresh();
  }

  /** The room line at the foot; redraws only when it changes. */
  setRoom(room: SummaryRoom): void {
    const key = JSON.stringify(room);
    if (key === this.roomKey) return;
    this.roomKey = key;
    this.room = room;
    this.panel.invalidate();
  }

  /** Data changed (a query was raised or closed). */
  refresh(): void {
    this.stats = summaryStats(this.view, this.filters);
    this.version++;
    this.panel.invalidate();
  }

  update(): void {
    this.panel.update();
  }

  /** Dev hook: the dashboards the numbers open, in drawing order. */
  regionTargets(): { id: string; dashboard: DashboardId }[] {
    return this.panel
      .allRegions()
      .filter((r) => r.data)
      .map((r) => ({ id: r.id, dashboard: (r.data as SummaryRegion).dashboard }));
  }

  selectRegion(id: string): boolean {
    const r = this.panel.allRegions().find((x) => x.id === id);
    const data = r?.data as SummaryRegion | undefined;
    if (!data) return false;
    this.handlers.project(data.dashboard);
    return true;
  }

  dispose(): void {
    this.panel.dispose();
  }

  private target(panel: CanvasPanel, id: string, r: Rect, dashboard: DashboardId, lines: string[]) {
    panel.region<SummaryRegion>({
      id,
      ...r,
      data: { dashboard },
      tooltip: [...lines, `Select to show ${DASHBOARD_TITLES[dashboard]} here`],
    });
  }

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    const s = this.stats;
    const study = this.view.file.study;
    panelBackground(ctx, W, H, 14);

    // Header: what this is, which study, and what it covers.
    text(ctx, 'STUDY SUMMARY', 20, 26, { size: 10, colour: PALETTE.accentText, weight: 700 });
    text(ctx, study.code, 20, 58, { size: 22, weight: 600, display: true });
    ctx.font = font(22, 600, true);
    const titleX = 20 + ctx.measureText(study.code).width + 14;
    const titleW = 470 - titleX;
    text(ctx, study.shortTitle, titleX, 56, {
      size: fitSize(ctx, study.shortTitle, titleW, 13, 9),
      colour: PALETTE.textSoft,
      maxWidth: titleW,
    });
    const scope = s.filtered
      ? `Room filter · ${describe(this.filters, this.view)}`
      : `Whole study · data cut ${study.dataCutDate}`;
    text(ctx, scope, W - 20, 26, {
      size: 10,
      weight: 600,
      colour: s.filtered ? PALETTE.teal : PALETTE.muted,
      align: 'right',
      maxWidth: 300,
    });
    text(ctx, `Phase ${study.phase} · ${study.therapeuticArea} · ${study.sponsor}`, W - 20, 56, {
      size: 10,
      colour: PALETTE.muted,
      align: 'right',
      maxWidth: 300,
    });

    // Left: the headline numbers, three columns of four.
    const tileW = 124;
    const tileH = 86;
    const cols = 3;
    s.stats.forEach((stat, i) => {
      const r = {
        x: 16 + (i % cols) * (tileW + 8),
        y: 76 + Math.floor(i / cols) * (tileH + 8),
        w: tileW,
        h: tileH,
      };
      const id = `stat:${i}`;
      glassPanel(ctx, r, 10, { hovered: panel.isHovered(id), hairline: false });
      const name = stat.name.toUpperCase();
      text(ctx, name, r.x + 10, r.y + 18, {
        size: fitSize(ctx, name, r.w - 18, 10, 8, 600),
        colour: PALETTE.muted,
        weight: 600,
        maxWidth: r.w - 18,
      });
      text(ctx, stat.value, r.x + 10, r.y + 52, {
        size: 26,
        weight: 600,
        display: true,
        colour: stat.alert ? PALETTE.amber : PALETTE.text,
        maxWidth: r.w - 18,
      });
      if (stat.sub)
        text(ctx, stat.sub, r.x + 10, r.y + 72, {
          size: fitSize(ctx, stat.sub, r.w - 18, 10, 8),
          colour: PALETTE.muted,
          maxWidth: r.w - 18,
        });
      this.target(panel, id, r, stat.dashboard, [
        stat.name,
        `${stat.value}${stat.sub ? ` · ${stat.sub}` : ''}`,
      ]);
    });

    // Centre: the sites in view on a globe, the study's design under it.
    const sites = this.view.file.sites.filter((x) => s.siteIds.includes(x.id));
    const gx = 16 + cols * (tileW + 8) + 8;
    const globe = { x: gx, y: 72, w: 260, h: 218 };
    drawGlobe(ctx, globe.x + globe.w / 2, globe.y + 98, 90, sites);
    text(ctx, `${s.sites} sites · ${s.countries} countries`, globe.x + globe.w / 2, globe.y + 210, {
      size: 11,
      weight: 600,
      align: 'center',
      colour: PALETTE.textSoft,
    });
    this.target(panel, 'globe', globe, 'Enrollment', [
      `${s.sites} sites in ${s.countries} countries`,
      sites.map((x) => x.city).join(', '),
    ]);

    this.drawDesign(ctx, panel, { x: gx, y: 296, w: globe.w, h: 108 });

    // Right: enrolment progress and the milestones.
    const rx = gx + globe.w + 16;
    const rw = W - rx - 16;
    const progress = { x: rx, y: 76, w: rw, h: 170 };
    if (panel.isHovered('progress')) fillRoundRect(ctx, progress, 10, PALETTE.hover);
    text(ctx, 'ENROLMENT PROGRESS', rx + 8, 96, {
      size: 10,
      weight: 700,
      colour: PALETTE.accentText,
    });
    text(ctx, `${Math.round(s.enrolledPct)}%`, rx + 8, 136, {
      size: 28,
      weight: 600,
      display: true,
    });
    text(ctx, `${s.enrolled} of ${s.target} randomised`, rx + 96, 128, {
      size: 10,
      colour: PALETTE.muted,
      maxWidth: rw - 104,
    });
    const bar = { x: rx + 96, y: 108, w: rw - 112, h: 6 };
    fillRoundRect(ctx, bar, 3, PALETTE.track);
    fillRoundRect(ctx, { ...bar, w: bar.w * Math.min(1, s.enrolledPct / 100) }, 3, PALETTE.teal);
    drawCumulative(ctx, rx + 8, 148, rw - 16, 90, s.curve);
    this.target(panel, 'progress', progress, 'Enrollment', [
      `${s.enrolled} of ${s.target} randomised (${s.enrolledPct}%)`,
      'Weekly cumulative enrolment against the plan (dashed)',
    ]);
    const milestones = { x: rx, y: 254, w: rw, h: 150 };
    if (panel.isHovered('milestones')) fillRoundRect(ctx, milestones, 10, PALETTE.hover);
    text(ctx, 'KEY MILESTONES', rx + 8, 274, { size: 10, weight: 700, colour: PALETTE.accentText });
    s.milestones.forEach((m, i) => {
      const y = 300 + i * 26;
      ctx.strokeStyle = m.done ? PALETTE.teal : PALETTE.line;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(rx + 16, y - 5, 7, 0, Math.PI * 2);
      ctx.stroke();
      if (m.done) {
        ctx.beginPath();
        ctx.moveTo(rx + 12, y - 5);
        ctx.lineTo(rx + 15, y - 2);
        ctx.lineTo(rx + 20, y - 9);
        ctx.stroke();
      }
      text(ctx, m.name, rx + 30, y, { size: 11, maxWidth: rw - 130 });
      text(ctx, m.when, rx + rw - 8, y, {
        size: 11,
        weight: 600,
        colour: m.done ? PALETTE.teal : PALETTE.muted,
        align: 'right',
      });
    });
    this.target(
      panel,
      'milestones',
      milestones,
      'Enrollment',
      s.milestones.map((m) => `${m.name}: ${m.when}`),
    );

    // Foot: subject status as one stacked bar with its key.
    const foot = { x: gx, y: 410, w: W - gx - 16, h: 66 };
    if (panel.isHovered('status')) fillRoundRect(ctx, foot, 10, PALETTE.hover);
    text(ctx, 'SUBJECT STATUS', foot.x + 8, foot.y + 18, {
      size: 10,
      weight: 700,
      colour: PALETTE.accentText,
    });
    const counts = SUBJECT_STATUSES.map((st) => ({
      status: st,
      n: this.view.subjects(this.filters).filter((x) => x.status === st).length,
    })).filter((c) => c.n > 0);
    const total = Math.max(1, s.screened);
    let x = foot.x + 8;
    const bw = foot.w - 16;
    for (const c of counts) {
      const w = (c.n / total) * bw;
      ctx.fillStyle = STATUS_COLOURS[c.status];
      ctx.fillRect(x, foot.y + 26, Math.max(1, w - 2), 12);
      x += w;
    }
    let kx = foot.x + 8;
    for (const c of counts) {
      const label = `${c.status} ${c.n}`;
      ctx.fillStyle = STATUS_COLOURS[c.status];
      ctx.fillRect(kx, foot.y + 48, 8, 8);
      ctx.font = font(9);
      const w = ctx.measureText(label).width;
      if (kx + 12 + w > foot.x + foot.w) break;
      text(ctx, label, kx + 12, foot.y + 56, { size: 9, colour: PALETTE.textSoft });
      kx += 12 + w + 14;
    }
    this.target(
      panel,
      'status',
      foot,
      'SubjectStatus',
      counts.map((c) => `${c.status}: ${c.n}`),
    );

    this.drawRoom(ctx, { x: 16, y: H - 30, w: W - 32, h: 22 });
  }

  /** What the study is: indication, intervention, design, arms and its start. */
  private drawDesign(ctx: CanvasRenderingContext2D, panel: CanvasPanel, r: Rect): void {
    const study = this.view.file.study;
    text(ctx, 'STUDY DESIGN', r.x + 8, r.y + 12, {
      size: 10,
      weight: 700,
      colour: PALETTE.accentText,
    });
    const facts: [string, string][] = [
      ['Indication', study.indication],
      ['Treatment', study.intervention],
      ['Design', `${study.design}${study.blinded ? ', blinded' : ', open label'}`],
      ['Arms', study.arms.map((a) => a.name).join(' · ')],
      ['Started', study.startDate],
    ];
    const labelW = 74;
    facts.forEach(([label, value], i) => {
      const y = r.y + 30 + i * 18;
      text(ctx, label.toUpperCase(), r.x + 8, y, {
        size: 8,
        weight: 700,
        colour: PALETTE.muted,
      });
      text(ctx, value, r.x + 8 + labelW, y, {
        size: fitSize(ctx, value, r.w - labelW - 12, 10, 7),
        colour: PALETTE.textSoft,
        maxWidth: r.w - labelW - 12,
      });
    });
    // Long values end in an ellipsis; pointing at the block shows them whole.
    panel.region({ id: 'design', ...r, tooltip: facts.map(([l, v]) => `${l}: ${v}`) });
  }

  /** The room: its code, the people in it (role colours), and that the meeting is written down. */
  private drawRoom(ctx: CanvasRenderingContext2D, r: Rect): void {
    const room = this.room;
    ctx.fillStyle = PALETTE.line;
    ctx.fillRect(r.x, r.y - 4, r.w, 1);
    if (!room) return;
    const y = r.y + 13;
    text(ctx, 'ROOM', r.x, y, { size: 9, weight: 700, colour: PALETTE.accentText });
    text(ctx, room.code, r.x + 42, y, { size: 11, weight: 700, display: true });
    let x = r.x + 92;
    const note = room.voiceTranscribed
      ? 'Transcribed: voice chat, Luma, queries and notes'
      : 'Written down: Luma, queries and notes';
    ctx.font = font(9);
    const noteW = ctx.measureText(note).width;
    const limit = r.x + r.w - noteW - 16;
    for (const [i, p] of room.people.entries()) {
      const label = `${p.name} · ${ROLE_STYLES[p.role].short}`;
      ctx.font = font(9, 600);
      const w = ctx.measureText(label).width + 22;
      if (x + w > limit) {
        text(ctx, `and ${room.people.length - i} more`, x, y, { size: 9, colour: PALETTE.muted });
        break;
      }
      fillRoundRect(ctx, { x, y: y - 11, w, h: 15 }, 7, PALETTE.faint);
      ctx.fillStyle = ROLE_STYLES[p.role].hex;
      ctx.beginPath();
      ctx.arc(x + 8, y - 3.5, 3, 0, Math.PI * 2);
      ctx.fill();
      text(ctx, label, x + 15, y, { size: 9, weight: 600, colour: PALETTE.textSoft });
      x += w + 6;
    }
    text(ctx, note, r.x + r.w, y, { size: 9, colour: PALETTE.muted, align: 'right' });
  }
}

function describe(f: DashboardFilters, view: StudyView): string {
  const parts: string[] = [];
  if (f.siteId) parts.push(view.siteLabel(f.siteId));
  if (f.arm) parts.push(f.arm);
  if (f.sex) parts.push(f.sex === 'F' ? 'female' : 'male');
  if (f.status) parts.push(f.status);
  if (f.subjectIds?.length) parts.push(`review ${f.subjectIds.join(', ')}`);
  if (f.subjectId) parts.push(`subject ${f.subjectId}`);
  if (f.dateFrom) parts.push(`from ${f.dateFrom}`);
  if (f.visitFrom || f.visitTo) parts.push(`visits ${f.visitFrom ?? '…'}–${f.visitTo ?? '…'}`);
  for (const k of ['flag', 'ageBand', 'bmiBucket'] as const) if (f[k]) parts.push(String(f[k]));
  return parts.join(' · ') || 'all';
}
